import { useEffect } from "react";
import { Link } from "react-router-dom";
import "./MeetUs.css";
import { 
  ArrowRight,
  Calendar,
  MapPin,
  Users,
  Handshake,
  Mail
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";


// Conference Data
const conferences = [
  {
    name: "Affiliate Summit West",
    location: "Las Vegas, NV",
    venue: "Caesars Forum",
    date: "January 2026",
    description: "The largest affiliate marketing event in the world. 7,000+ attendees, networking opportunities, and cutting-edge performance marketing insights.",
    attendees: "7,000+",
    highlight: "Flagship Event"
  },
  {
    name: "Affiliate Summit East",
    location: "New York, NY",
    venue: "NY Marriott Marquis",
    date: "July 2026",
    description: "The original performance marketing event on the East Coast. Connect with 4,500+ e-commerce sellers, media buyers, and affiliate marketers.",
    attendees: "4,500+",
    highlight: "East Coast"
  },
  {
    name: "LeadsCon",
    location: "Las Vegas, NV",
    venue: "Paris Las Vegas",
    date: "April 2026",
    description: "The premier event for lead generation and performance marketing professionals. Deep-dive sessions on lead quality, conversion optimization, and data strategies.",
    attendees: "3,000+",
    highlight: "Lead Gen Focus"
  },
  {
    name: "Affiliate World Dubai",
    location: "Dubai, UAE",
    venue: "Dubai World Trade Centre",
    date: "February 2026",
    description: "Global affiliate networking at its finest. Connect with international performance marketers and explore emerging markets.",
    attendees: "5,000+",
    highlight: "Global"
  },
  {
    name: "Lead Generation World",
    location: "San Diego, CA",
    venue: "San Diego Convention Center",
    date: "January 2026",
    description: "Focused exclusively on lead generation strategies, technology, and best practices. Perfect for brands serious about scaling customer acquisition.",
    attendees: "2,000+",
    highlight: "Specialized"
  },
  {
    name: "Performance Marketing World",
    location: "Phoenix, AZ",
    venue: "Phoenix Convention Center",
    date: "March 2026",
    description: "Emerging conference dedicated to the latest trends in performance marketing, attribution, and data-driven customer acquisition.",
    attendees: "1,500+",
    highlight: "New"
  }
];

// Hero Section
const MeetHero = () => {
  return (
    <section className="meet-hero" data-testid="meet-hero">
      <div className="meet-hero-content">
        <span className="meet-label">MEET US</span>
        <h1>Let's Connect at Industry Events</h1>
        <p>
          We're active participants in the performance marketing community. 
          Find us at these leading conferences to discuss how data-driven 
          marketing can transform your customer acquisition strategy.
        </p>
        <Button 
          className="btn-primary-zeta"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          Schedule a Meeting
          <ArrowRight size={18} />
        </Button>
      </div>
    </section>
  );
};

// Stats Section
const MeetStats = () => {
  const stats = [
    { value: '245M+', label: 'Consumer Profiles' },
    { value: '1B+', label: 'Monthly Ad Interactions' },
    { value: '3.5x', label: 'Average Client ROI' },
    { value: '33%', label: 'Below CPA Targets' },
  ];

  return (
    <section className="meet-stats" data-testid="meet-stats">
      <div className="meet-stats-grid">
        {stats.map((stat, i) => (
          <div key={i} className="meet-stat-card">
            <span className="meet-stat-value">{stat.value}</span>
            <span className="meet-stat-label">{stat.label}</span>
          </div>
        ))}
      </div>
    </section>
  );
};

// Conferences Grid Section
const ConferencesSection = () => {
  return (
    <section className="conferences-section" data-testid="conferences-section">
      <div className="conferences-content">
        <div className="section-header">
          <span className="section-label">2026 CONFERENCE SCHEDULE</span>
          <h2>Where You'll Find Us</h2>
          <p>
            Our team attends the most influential performance marketing events. 
            Book time with us to discuss your customer acquisition goals.
          </p>
        </div>
        
        <div className="conferences-grid">
          {conferences.map((conf, i) => (
            <div key={i} className="conference-card" data-testid={`conference-${i}`}>
              <div className="conference-header">
                <span className="conference-highlight">{conf.highlight}</span>
                <h3>{conf.name}</h3>
              </div>
              
              <div className="conference-details">
                <div className="conference-detail">
                  <Calendar size={16} />
                  <span>{conf.date}</span>
                </div>
                <div className="conference-detail">
                  <MapPin size={16} />
                  <span>{conf.location}</span>
                </div>
                <div className="conference-detail">
                  <Users size={16} />
                  <span>{conf.attendees} Attendees</span>
                </div>
              </div>
              
              <p className="conference-description">{conf.description}</p>
              
              <div className="conference-venue">
                <span>Venue: {conf.venue}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Why Meet Us Section
const WhyMeetSection = () => {
  const reasons = [
    {
      icon: Handshake,
      title: "Face-to-Face Strategy Sessions",
      description: "Nothing beats in-person conversations. Meet our team to discuss your specific challenges and opportunities in customer acquisition."
    },
    {
      icon: Users,
      title: "Meet Our Leadership",
      description: "Our founders and senior team attend these events. Get direct access to the people who built IRIS and drive our strategy."
    },
    {
      icon: Calendar,
      title: "Book Ahead for Priority",
      description: "Our calendars fill up fast at conferences. Reach out before the event to secure dedicated time with our team."
    }
  ];

  return (
    <section className="why-meet-section" data-testid="why-meet">
      <div className="why-meet-content">
        <div className="section-header">
          <span className="section-label">WHY MEET IN PERSON</span>
          <h2>Get More Than a Business Card</h2>
        </div>
        
        <div className="why-meet-grid">
          {reasons.map((reason, i) => (
            <div key={i} className="why-meet-card">
              <div className="why-meet-icon">
                <reason.icon size={24} />
              </div>
              <h3>{reason.title}</h3>
              <p>{reason.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section
const MeetCTA = () => (
  <section className="meet-cta-section" data-testid="meet-cta">
    <div className="meet-cta-content">
      <h2>Can't Wait for a Conference?</h2>
      <p>
        Schedule a virtual meeting now to discuss how IntentLogic can 
        help you achieve your customer acquisition goals.
      </p>
      <div className="cta-buttons">
        <Button 
          className="btn-primary-zeta"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          <Mail size={18} />
          Contact Us
        </Button>
      </div>
    </div>
  </section>
);

// Main Meet Us Page Component
function MeetUsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Layout>
      <div className="meet-page" data-testid="meet-us-page">
        <SEO 
          title="Meet Us - Events & Conferences"
          description="Meet the IntentLogic team at industry conferences and events. Connect with our marketing experts to discuss data-driven solutions for your business."
          keywords="meet IntentLogic, marketing events, industry conferences, networking, marketing team, business meetings, trade shows"
          canonical="/meet-us"
        />
        <MeetHero />
        <MeetStats />
        <ConferencesSection />
        <WhyMeetSection />
        <MeetCTA />
      </div>
    </Layout>
  );
}

export default MeetUsPage;
